import { createContext, useContext, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useAchievements, getEligibleKeys } from '@/hooks/useAchievements';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { getAchievementByKey } from '@/lib/achievements';
import { supabase } from '@/lib/supabase';
import type { AchievementWithStatus } from '@/lib/achievements';

interface AchievementsContextValue {
  loading: boolean;
  achievements: AchievementWithStatus[];
  achievementsByCategory: ReturnType<typeof useAchievements>['achievementsByCategory'];
  recentlyUnlocked: AchievementWithStatus[];
  stats: ReturnType<typeof useAchievements>['stats'];
  unlockAchievement: (key: string) => Promise<boolean>;
  markAsSeen: () => Promise<void>;
  checkAndUnlock: (key: string) => Promise<void>;
  refresh: () => void;
}

const AchievementsContext = createContext<AchievementsContextValue | null>(null);

export function AchievementsProvider({ children }: { children: ReactNode }) {
  const hook = useAchievements();
  const { toast } = useToast();
  const { language } = useTranslation();
  const shownToasts = useRef<Set<string>>(new Set());
  const lastCheckedCounters = useRef<string>('');

  // Check and unlock with feedback
  const checkAndUnlock = useCallback(
    async (key: string) => {
      const success = await hook.unlockAchievement(key);
      if (success) {
        const def = getAchievementByKey(key);
        if (def && !shownToasts.current.has(key)) {
          shownToasts.current.add(key);
          toast({
            title: `${def.icon} ${language === 'fr' ? 'Succès débloqué !' : 'Achievement unlocked!'}`,
            description: language === 'fr' ? def.nameFr : def.nameEn,
          });
        }
      }
    },
    [hook.unlockAchievement, toast, language]
  );

  // Auto-check all achievements whenever server counters change
  useEffect(() => {
    if (hook.loading || !hook.countersLoaded) return;

    // Only re-run if counters actually changed
    const countersKey = JSON.stringify(hook.counters);
    if (countersKey === lastCheckedCounters.current) return;
    lastCheckedCounters.current = countersKey;

    const runAutoCheck = async () => {
      // Check profile completeness
      let profileComplete = false;
      try {
        const {
          data: { user: supaUser },
        } = await supabase.auth.getUser();
        const meta = supaUser?.user_metadata;
        if (meta?.name && meta?.phone) profileComplete = true;
      } catch {
        /* ignore */
      }

      const eligibleKeys = getEligibleKeys(hook.counters, profileComplete);

      // Sequentially check each eligible key (checkAndUnlock skips already-unlocked)
      for (const key of eligibleKeys) {
        await checkAndUnlock(key);
      }
    };

    runAutoCheck();
  }, [hook.loading, hook.countersLoaded, hook.counters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for mutation events from other hooks to trigger re-check
  useEffect(() => {
    const handleMutation = () => hook.refresh();
    window.addEventListener('achievements-need-refresh', handleMutation);
    return () => window.removeEventListener('achievements-need-refresh', handleMutation);
  }, [hook.refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AchievementsContext.Provider
      value={{
        loading: hook.loading,
        achievements: hook.achievements,
        achievementsByCategory: hook.achievementsByCategory,
        recentlyUnlocked: hook.recentlyUnlocked,
        stats: hook.stats,
        unlockAchievement: hook.unlockAchievement,
        markAsSeen: hook.markAsSeen,
        checkAndUnlock,
        refresh: hook.refresh,
      }}
    >
      {children}
    </AchievementsContext.Provider>
  );
}

export function useAchievementsContext() {
  const ctx = useContext(AchievementsContext);
  if (!ctx) {
    // Return a no-op context for pages outside the provider (public pages)
    return {
      loading: false,
      achievements: [],
      achievementsByCategory: [],
      recentlyUnlocked: [],
      stats: {
        unlocked: 0,
        total: 0,
        unseenCount: 0,
        nextAchievement: null as unknown as AchievementWithStatus,
      },
      unlockAchievement: async () => false,
      markAsSeen: async () => {},
      checkAndUnlock: async () => {},
      refresh: () => {},
    } as AchievementsContextValue;
  }
  return ctx;
}
